import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEO, { buildBreadcrumbSchema } from '@/components/SEO';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const privacySchema = [
  buildBreadcrumbSchema([
    { name: 'Home', href: '/' },
    { name: 'Privacy Policy', href: '/privacy' },
  ]),
  {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy | Fore Capital',
  url: `${SITE_URL}/privacy`,
  description: 'Fore Capital Privacy Policy. Learn how we collect, use, disclose, and safeguard your personal information when you visit our website or use our services.',
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#F9F9F7]" style={{ fontFamily: "'Google Sans Flex', sans-serif" }}>
      <SEO
        title="Privacy Policy | Fore Capital"
        description="Fore Capital Privacy Policy. Learn how we collect, use, disclose, and safeguard your personal information when you visit our website or use our services."
        keywords="privacy policy, data protection, personal information, Fore Capital privacy"
        pathname="/privacy"
        schema={privacySchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="bg-[#122852] py-20 px-5 md:px-[75px]">
          <div className="max-w-[1400px] mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 mb-6" aria-label="Breadcrumb">
              <a
                href="/"
                className="text-white/40 text-[11px] uppercase tracking-[0.18em] hover:text-white/70 transition-colors duration-200 cursor-pointer"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Home
              </a>
              <span className="text-white/25 text-[11px]">/</span>
              <span
                className="text-[#d3b366] text-[11px] uppercase tracking-[0.18em]"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Privacy Policy
              </span>
            </nav>
            <p
              className="text-[#d3b366] text-[10px] uppercase tracking-[0.25em] font-medium mb-4"
              style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
            >
              Legal
            </p>
            <h1
              className="text-white text-3xl md:text-4xl lg:text-[48px] font-light leading-[1.1]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Privacy Policy
            </h1>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 px-5 md:px-[75px]">
          <div className="max-w-[900px] mx-auto">
            <div className="space-y-12">
              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  1. Introduction
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  Fore Capital LLC ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  2. Information We Collect
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed mb-3"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  We may collect the following types of information:
                </p>
                <ul className="space-y-2 text-[#1A1A1A]/60 text-[15px] leading-relaxed" style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span><strong className="text-[#1A1A1A]/80">Personal Information:</strong> Name, email address, phone number, business name, and billing information.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span><strong className="text-[#1A1A1A]/80">Usage Data:</strong> IP address, browser type, pages visited, time spent on pages, and referring URLs.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span><strong className="text-[#1A1A1A]/80">Transaction Data:</strong> Payment processing information, transaction history, and account activity.</span>
                  </li>
                </ul>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  3. How We Use Your Information
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed mb-3"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  We use the information we collect to:
                </p>
                <ul className="space-y-2 text-[#1A1A1A]/60 text-[15px] leading-relaxed" style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Provide, operate, and maintain our services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Process transactions and send related information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Communicate with you about updates, support, and marketing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Improve our website and services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Comply with legal obligations and prevent fraud</span>
                  </li>
                </ul>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  4. Information Sharing
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  We do not sell your personal information. We may share information with trusted third-party service providers who assist us in operating our business, processing payments, or delivering services. All third parties are contractually obligated to maintain the confidentiality and security of your information.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  5. Data Security
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  We implement industry-standard security measures including encryption, access controls, and regular security audits to protect your information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  6. Your Rights
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  Depending on your location, you may have the right to access, correct, delete, or restrict the processing of your personal information. To exercise these rights, please contact us at privacy@forecapital.com.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  7. Changes to This Policy
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Modified" date. We encourage you to review this policy periodically.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  8. Contact Us
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  If you have any questions about this Privacy Policy, please contact us at privacy@forecapital.com or through our <a href="/contact" className="text-[#a8894a] hover:underline cursor-pointer">Contact page</a>.
                </p>
              </div>

              <div className="pt-8 border-t border-[#1A1A1A]/10">
                <p
                  className="text-[#1A1A1A]/40 text-xs"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  Last Modified: April 24, 2026
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}